/* <bb-product-recs>: PDP recs (blueprint §3.14, PR §7). Card selects and adds are bb-cart.js's.
   Runs before bb-cart.js (both deferred), so it waits for window.BBCart. */
(() => {
  if (!window.customElements || customElements.get('bb-product-recs')) return;

  const RM = matchMedia('(prefers-reduced-motion: reduce)'), VAL = { attributes: true, attributeFilter: ['value'] };
  let warned = false, ready = null, kb = 0;
  const warn = s => { if (!warned) { warned = true; console.warn('[bb-recs]', s); } };
  const lc = s => String(s || '').trim().toLowerCase();

  // Gives up after 10 s, so a broken cart script hides the grey skeletons.
  const whenCart = () => ready || (ready = new Promise(res => {
    if (window.BBCart) return res(window.BBCart);
    const t = setTimeout(() => res(window.BBCart), 10000);
    document.addEventListener('bb:cart:ready', () => { clearTimeout(t); res(window.BBCart); }, { once: true });
  }));

  addEventListener('keydown', () => kb = 1, true);
  addEventListener('pointerdown', () => kb = 0, true);

  class BBProductRecs extends HTMLElement {
    connectedCallback() {
      // Shipped hidden for no-JS shoppers; a hidden box would never intersect.
      this.sec = this.closest('.bb-sec--recs');
      if (this.sec) this.sec.hidden = false;
      if (this.started) {
        // Moved after loading: re-arm.
        if (this.onResize) addEventListener('resize', this.onResize);
        if (this.mo) this.mo.observe(this.input, VAL);
        return;
      }
      const go = () => { this.started = true; whenCart().then(c => this.load(c)).catch(e => this.hide(e && e.message)); };
      if (this.hasAttribute('data-eager') || !('IntersectionObserver' in window)) return go();
      this.io = new IntersectionObserver(es => {
        if (es.some(e => e.isIntersecting)) { this.io.disconnect(); go(); }
      }, { rootMargin: '0px 0px 600px 0px' });
      this.io.observe(this);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.mo) this.mo.disconnect();
      if (this.onResize) removeEventListener('resize', this.onResize);
    }

    // CLS 0: collapse only below the viewport, else keep the space.
    hide(why) {
      const s = this.sec;
      if (why != null) warn(why);
      if (s) s.getBoundingClientRect().top > innerHeight ? s.hidden = true : s.style.visibility = 'hidden';
      this.setAttribute('aria-busy', 'false');
    }

    async load(C) {
      if (!C || !C.recs) return this.hide('no BBCart');
      const d = this.dataset, ac = new AbortController(), t = setTimeout(() => ac.abort(), 8000);
      let html;
      try {
        const r = await fetch(d.url, { credentials: 'same-origin', signal: ac.signal });
        if (!r.ok) return this.hide(r.status);
        html = await r.text();
      } catch (e) {
        return this.hide(e && e.name === 'AbortError' ? 'timeout' : 0);
      } finally {
        clearTimeout(t);
      }
      if (!this.isConnected) return;   // replaced meanwhile (theme editor re-render)
      try { this.anchor = JSON.parse(this.querySelector('[data-bb-recs-anchor]').textContent); } catch (e) { this.anchor = { variants: {} }; }
      const a = lc(this.anchor.title), tw = ['slim ' + a, a.startsWith('slim ') && a.slice(5)];
      const nodes = [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-bb-recs-payload] [data-bb-rec]')].filter(n => !tw.includes(lc(n.dataset.title)));
      const kept = C.recs.pick(nodes, { anchorId: Number(d.anchorId), anchorType: d.anchorType || '', aliases: d.typeAliases || '', max: Number(d.max) || 12 });
      if (!kept.length) return this.hide();

      this.track = this.querySelector('.bb-recs__track');
      this.track.replaceChildren(...kept.map(n => document.importNode(n, true)));
      this.setAttribute('aria-busy', 'false');
      d.count = kept.length;

      const form = document.querySelector('main .js-product-form');
      this.input = form && form.querySelector('input[name="id"]');
      this.match(C);
      if (this.input) {
        // Size changes land in the value attribute (as product-form.liquid expects).
        this.mo = new MutationObserver(() => this.match(C));
        this.mo.observe(this.input, VAL);
      }
      this.carousel();

      const recCount = kept.filter(n => n.dataset.bbSrc === 'rec').length;
      document.dispatchEvent(new CustomEvent('bb:recs:rendered', { detail: { context: 'pdp', count: kept.length, recCount, topupCount: kept.length - recCount } }));
    }

    // Untouched card selects follow the PDP size (Rebuy match_variant).
    match(C) {
      const a = this.anchor, v = a.variants[this.input ? this.input.value : a.selected] || a.variants[a.selected];
      if (v) C.recs.match(this.track, { title: v.title, option1: v.option1 });
    }

    carousel() {
      const t = this.track, n = t.children.length, dots = this.querySelector('.bb-recs__dots');
      const prev = this.querySelector('.bb-recs__arrow--prev'), next = this.querySelector('.bb-recs__arrow--next');
      let pages = 1, cols = 4, raf = 0, tm = 0;
      // A page is cols cards (phones also show the gutters and the next card's edge).
      const step = () => t.firstElementChild.getBoundingClientRect().width * cols || t.clientWidth;
      const sync = () => {
        raf = 0;
        const w = t.clientWidth, x = t.scrollLeft, end = x + w >= t.scrollWidth - 1;
        prev.hidden = next.hidden = t.scrollWidth <= w + 1;
        // A short last page never scrolls a full width.
        const i = end ? pages - 1 : Math.min(pages - 1, Math.round(x / step()));
        [...dots.children].forEach((el, j) => el.classList.toggle('bb-recs__dot--active', j === i));
      };
      const build = () => {
        // Columns (4/3/2) come from the CSS.
        cols = parseInt(getComputedStyle(this).getPropertyValue('--bb-recs-cols'), 10) || 4;
        pages = Math.max(1, Math.ceil(n / cols));
        dots.innerHTML = pages > 1 ? '<span class="bb-recs__dot"></span>'.repeat(pages) : '';
        sync();
      };
      const page = dir => {
        const w = t.clientWidth, x = t.scrollLeft, max = t.scrollWidth - w, wrap = dir < 0 ? x <= 1 : x >= max - 1;
        t.scrollTo({ left: wrap ? (dir < 0 ? max : 0) : x + dir * step(), behavior: wrap || RM.matches ? 'auto' : 'smooth' });
      };
      prev.addEventListener('click', () => page(-1));
      next.addEventListener('click', () => page(1));
      t.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(sync); }, { passive: true });
      t.addEventListener('focusin', e => {
        const c = kb && e.target.closest('[data-bb-rec]'), r = c && c.getBoundingClientRect(), k = t.getBoundingClientRect();
        const d = r && r.right > k.left && r.left < k.right && (Math.max(0, r.right - k.right) || Math.min(0, r.left - k.left));
        if (d) t.scrollBy({ left: d, behavior: RM.matches ? 'auto' : 'smooth' });
      });
      this.onResize = () => { clearTimeout(tm); tm = setTimeout(build, 150); };
      addEventListener('resize', this.onResize);
      build();
    }
  }

  customElements.define('bb-product-recs', BBProductRecs);
})();
